<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { DURATION_UNITS } from 'dashboard/components-next/input/constants';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import DurationInput from 'dashboard/components-next/input/DurationInput.vue';
import SingleSelect from 'dashboard/components-next/filter/inputs/SingleSelect.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import packagesModule from 'shipping/store/modules/packages';

const { t } = useI18n();
const store = useStore();
const { currentAccount, updateAccount } = useAccount();

if (!store.hasModule('shippingPackages')) {
  store.registerModule('shippingPackages', packagesModule);
}

const isEnabled = ref(false);
const platformId = ref(null);
const shippingId = ref(null);
const defaultPackageId = ref(null);
const checkoutDuration = ref(null);
const durationUnit = ref(DURATION_UNITS.HOURS);
const isSubmitting = ref(false);

const integrations = useMapGetter('integrations/getAppIntegrations');
const packages = computed(
  () => store.getters['shippingPackages/getPackages'] || []
);

const platformOptions = computed(() =>
  (integrations.value || [])
    .filter(i => i.category === 'checkout_platform' && i.enabled)
    .map(i => ({ id: i.id, name: i.name }))
);

const shippingOptions = computed(() =>
  (integrations.value || [])
    .filter(i => i.category === 'shipping' && i.enabled)
    .map(i => ({ id: i.id, name: i.name }))
);

const packageOptions = computed(() =>
  packages.value.map(p => ({ id: p.id, name: p.name }))
);

const selectedPlatform = computed({
  get: () => platformOptions.value.find(o => o.id === platformId.value) ?? null,
  set: v => {
    platformId.value = v?.id ?? null;
  },
});

const selectedShipping = computed({
  get: () => shippingOptions.value.find(o => o.id === shippingId.value) ?? null,
  set: v => {
    shippingId.value = v?.id ?? null;
  },
});

const selectedPackage = computed({
  get: () =>
    packageOptions.value.find(o => o.id === defaultPackageId.value) ?? null,
  set: v => {
    defaultPackageId.value = v?.id ?? null;
  },
});

watch(
  currentAccount,
  account => {
    const s = account?.settings || {};
    isEnabled.value = s.checkout_enabled ?? false;
    platformId.value = s.checkout_platform_id ?? null;
    shippingId.value = s.checkout_shipping_integration_id ?? null;
    defaultPackageId.value = s.checkout_default_package_id ?? null;

    const dur = s.checkout_duration;
    if (dur) {
      checkoutDuration.value = dur;
      if (dur % (24 * 60) === 0) durationUnit.value = DURATION_UNITS.DAYS;
      else if (dur % 60 === 0) durationUnit.value = DURATION_UNITS.HOURS;
      else durationUnit.value = DURATION_UNITS.MINUTES;
    }
  },
  { immediate: true }
);

onMounted(() => {
  store.dispatch('integrations/get');
  store.dispatch('shippingPackages/get');
});

const toggleEnabled = async () => {
  if (!isEnabled.value) {
    try {
      await updateAccount({ checkout_enabled: false }, { silent: true });
    } catch {
      useAlert(t('CHECKOUT_SETTINGS.SETTINGS.SAVE_ERROR'));
    }
  }
};

const handleSubmit = async () => {
  try {
    isSubmitting.value = true;
    await updateAccount(
      {
        checkout_enabled: isEnabled.value,
        checkout_platform_id: platformId.value,
        checkout_shipping_integration_id: shippingId.value,
        checkout_default_package_id: defaultPackageId.value,
        checkout_duration: checkoutDuration.value,
      },
      { silent: true }
    );
    useAlert(t('CHECKOUT_SETTINGS.SETTINGS.SAVE_SUCCESS'));
  } catch {
    useAlert(t('CHECKOUT_SETTINGS.SETTINGS.SAVE_ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <div
    class="flex flex-col w-full outline-1 outline outline-n-container rounded-xl bg-n-solid-2 divide-y divide-n-weak"
  >
    <!-- Header row with toggle -->
    <div class="flex flex-col gap-2 items-start px-5 py-4">
      <div class="flex justify-between items-center w-full">
        <h3 class="text-heading-2 text-n-slate-12">
          {{ t('CHECKOUT_SETTINGS.SETTINGS.ENABLED.LABEL') }}
        </h3>
        <Switch v-model="isEnabled" @change="toggleEnabled" />
      </div>
      <p class="mb-0 text-body-para text-n-slate-11">
        {{ t('CHECKOUT_SETTINGS.SETTINGS.ENABLED.NOTE') }}
      </p>
    </div>

    <!-- Config form, visible only when enabled -->
    <div v-if="isEnabled" class="px-5 py-4">
      <form class="grid gap-5" @submit.prevent="handleSubmit">
        <WithLabel
          :label="t('CHECKOUT_SETTINGS.SETTINGS.PLATFORM.LABEL')"
          :help-message="t('CHECKOUT_SETTINGS.SETTINGS.PLATFORM.HELP')"
        >
          <SingleSelect
            v-model="selectedPlatform"
            :options="platformOptions"
            :placeholder="t('CHECKOUT_SETTINGS.SETTINGS.PLATFORM.PLACEHOLDER')"
            placeholder-icon="i-lucide-chevron-down"
            placeholder-trailing-icon
            variant="faded"
          />
        </WithLabel>

        <WithLabel
          :label="t('CHECKOUT_SETTINGS.SETTINGS.SHIPPING.LABEL')"
          :help-message="t('CHECKOUT_SETTINGS.SETTINGS.SHIPPING.HELP')"
        >
          <SingleSelect
            v-model="selectedShipping"
            :options="shippingOptions"
            :placeholder="t('CHECKOUT_SETTINGS.SETTINGS.SHIPPING.PLACEHOLDER')"
            placeholder-icon="i-lucide-chevron-down"
            placeholder-trailing-icon
            variant="faded"
          />
        </WithLabel>

        <WithLabel
          :label="t('CHECKOUT_SETTINGS.SETTINGS.PACKAGE.LABEL')"
          :help-message="t('CHECKOUT_SETTINGS.SETTINGS.PACKAGE.HELP')"
        >
          <SingleSelect
            v-model="selectedPackage"
            :options="packageOptions"
            :placeholder="t('CHECKOUT_SETTINGS.SETTINGS.PACKAGE.PLACEHOLDER')"
            placeholder-icon="i-lucide-chevron-down"
            placeholder-trailing-icon
            variant="faded"
          />
        </WithLabel>

        <WithLabel
          :label="t('CHECKOUT_SETTINGS.SETTINGS.DURATION.LABEL')"
          :help-message="t('CHECKOUT_SETTINGS.SETTINGS.DURATION.HELP')"
        >
          <div class="gap-2 w-full grid grid-cols-[3fr_1fr]">
            <DurationInput
              v-model="checkoutDuration"
              v-model:unit="durationUnit"
              min="1"
              class="w-full"
            />
          </div>
        </WithLabel>

        <div class="flex gap-2">
          <NextButton
            blue
            type="submit"
            :is-loading="isSubmitting"
            :label="t('CHECKOUT_SETTINGS.SETTINGS.SAVE_BUTTON')"
          />
        </div>
      </form>
    </div>
  </div>
</template>
